/**
 * Mac Desktop Manager Component
 * Lists all macOS desktops with phase-aware action buttons (start/stop/terminate)
 */

import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Spin,
  Empty,
  Modal,
  message,
  Tooltip
} from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  DesktopOutlined,
  PlusOutlined,
  ReloadOutlined,
  LinkOutlined,
  CloudServerOutlined,
  ExclamationCircleOutlined,
  AppleFilled,
  ClockCircleOutlined
} from "@ant-design/icons";
import {
  fetchMacDesktops,
  performMacAction
} from "./store/macSlice";
import "./MacDesktopManager.scss";

const { Title, Text } = Typography;
const { confirm } = Modal;

/** Map desktop phase -> tag color + label */
const PHASE_CONFIG = {
  Running: { color: "success", label: "Running", icon: <PlayCircleOutlined /> },
  Stopped: { color: "default", label: "Stopped", icon: <PauseCircleOutlined /> },
  Pending: { color: "processing", label: "Pending", icon: <ClockCircleOutlined /> },
  Provisioning: { color: "processing", label: "Provisioning", icon: <CloudServerOutlined /> },
  Starting: { color: "processing", label: "Starting", icon: <PlayCircleOutlined /> },
  Stopping: { color: "warning", label: "Stopping", icon: <PauseCircleOutlined /> },
  Terminating: { color: "warning", label: "Terminating", icon: <DeleteOutlined /> },
  Failed: { color: "error", label: "Failed", icon: <ExclamationCircleOutlined /> }
};

/** Phases where the desktop is transitioning -- disable actions */
const TRANSITIONING_PHASES = ["Pending", "Provisioning", "Starting", "Stopping", "Terminating"];

function MacDesktopManager() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { desktops, loading, actionLoading } = useSelector((state) => state.macos);

  /* --------- Fetch desktops on mount + polling --------- */

  const loadDesktops = useCallback(() => {
    dispatch(fetchMacDesktops());
  }, [dispatch]);

  useEffect(() => {
    loadDesktops();
    const interval = setInterval(loadDesktops, 10000);
    return () => clearInterval(interval);
  }, [loadDesktops]);

  /* --------- Action handlers --------- */

  const handleAction = useCallback(
    async (id, action) => {
      try {
        await dispatch(performMacAction({ id, action })).unwrap();
        const labels = { start: "Starting", stop: "Stopping", terminate: "Terminating" };
        message.success(`Desktop is ${labels[action] || action}...`);
        // Re-fetch to get updated phase
        dispatch(fetchMacDesktops());
      } catch (error) {
        message.error(error?.message || `Failed to ${action} desktop`);
      }
    },
    [dispatch]
  );

  const confirmStop = useCallback(
    (desktop) => {
      confirm({
        centered: true,
        title: "Stop Desktop",
        icon: <PauseCircleOutlined style={{ color: "#faad14" }} />,
        content: (
          <div>
            <p>
              Are you sure you want to stop <strong>{desktop.name || desktop.id}</strong>?
            </p>
            <p style={{ color: "#888", fontSize: 13 }}>
              The instance will be paused. Your data is preserved and you can start it again later.
              You will stop paying for compute while stopped.
            </p>
          </div>
        ),
        okText: "Stop Desktop",
        okType: "primary",
        cancelText: "Cancel",
        maskClosable: true,
        onOk: () => handleAction(desktop.id, "stop")
      });
    },
    [handleAction]
  );

  const confirmTerminate = useCallback(
    (desktop) => {
      confirm({
        centered: true,
        title: "Terminate Desktop",
        icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
        content: (
          <div>
            <p>
              Are you sure you want to permanently delete{" "}
              <strong>{desktop.name || desktop.id}</strong>?
            </p>
            <p style={{ color: "#ff4d4f", fontSize: 13 }}>
              This action cannot be undone. The EC2 instance and all data will be destroyed.
            </p>
          </div>
        ),
        okText: "Terminate",
        okType: "danger",
        cancelText: "Cancel",
        maskClosable: true,
        onOk: () => handleAction(desktop.id, "terminate")
      });
    },
    [handleAction]
  );

  /* --------- Render helpers --------- */

  const renderPhaseTag = (phase) => {
    const config = PHASE_CONFIG[phase] || { color: "default", label: phase, icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  const isTransitioning = (phase) => TRANSITIONING_PHASES.includes(phase);
  const isActionLoading = (id) => !!actionLoading?.[id];

  const renderActions = (desktop) => {
    const { id, phase } = desktop;
    const busy = isTransitioning(phase) || isActionLoading(id);

    return (
      <Space size="small" wrap>
        {/* Connect -- only when Running */}
        {phase === "Running" && (
          <Tooltip title="Connect to desktop">
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => navigate(`/control/macos/${id}`)}
              disabled={busy}
            >
              Connect
            </Button>
          </Tooltip>
        )}

        {/* Start -- only when Stopped and persistent */}
        {phase === "Stopped" && desktop.persistent !== false && (
          <Tooltip title="Start the stopped desktop">
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => handleAction(id, "start")}
              loading={isActionLoading(id)}
              disabled={busy}
            >
              Start
            </Button>
          </Tooltip>
        )}

        {/* Stop -- only when Running and persistent */}
        {phase === "Running" && desktop.persistent !== false && (
          <Tooltip title="Pause the desktop (saves costs)">
            <Button
              icon={<PauseCircleOutlined />}
              onClick={() => confirmStop(desktop)}
              disabled={busy}
            >
              Stop
            </Button>
          </Tooltip>
        )}

        {/* Terminate -- when Running, Stopped, or Failed */}
        {["Running", "Stopped", "Failed"].includes(phase) && (
          <Tooltip title="Permanently delete this desktop">
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmTerminate(desktop)}
              disabled={busy}
            >
              Terminate
            </Button>
          </Tooltip>
        )}

        {/* Transitioning spinner */}
        {isTransitioning(phase) && (
          <Space>
            <Spin size="small" />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {phase}...
            </Text>
          </Space>
        )}
      </Space>
    );
  };

  /* --------- Render --------- */

  return (
    <div className="mac-manager-container">
      {/* Header */}
      <div className="manager-header">
        <div className="manager-header-left">
          <AppleFilled className="manager-icon" />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              macOS Desktops
            </Title>
            <Text type="secondary">Manage your macOS cloud desktops</Text>
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadDesktops} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/launch/macos")}
          >
            Launch New Desktop
          </Button>
        </Space>
      </div>

      {/* Desktop list */}
      <div className="manager-grid">
        {loading && desktops.length === 0 ? (
          <div className="manager-loading">
            <Spin size="large" />
            <Text type="secondary">Loading desktops...</Text>
          </div>
        ) : desktops.length === 0 ? (
          <Empty
            image={<DesktopOutlined style={{ fontSize: 64, color: "#ccc" }} />}
            description="No macOS desktops yet"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/launch/macos")}
            >
              Launch Your First Desktop
            </Button>
          </Empty>
        ) : (
          desktops.map((desktop) => (
            <Card key={desktop.id} className={`desktop-card phase-${(desktop.phase || "").toLowerCase()}`}>
              <div className="desktop-card-body">
                <div className="desktop-card-info">
                  <div className="desktop-card-header">
                    <DesktopOutlined className="desktop-card-icon" />
                    <div>
                      <Text strong className="desktop-card-name">
                        {desktop.name || desktop.id}
                      </Text>
                      {renderPhaseTag(desktop.phase)}
                      {desktop.persistent === false && (
                        <Tag color="orange">Ephemeral</Tag>
                      )}
                    </div>
                  </div>

                  <div className="desktop-card-meta">
                    {desktop.instanceType && (
                      <Text type="secondary">
                        <CloudServerOutlined /> {desktop.instanceType}
                      </Text>
                    )}
                    {desktop.createdAt && (
                      <Text type="secondary">
                        <ClockCircleOutlined />{" "}
                        {new Date(desktop.createdAt).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </Text>
                    )}
                    {desktop.publicIp && (
                      <Text type="secondary" copyable={{ text: desktop.publicIp }}>
                        IP: {desktop.publicIp}
                      </Text>
                    )}
                  </div>
                </div>

                <div className="desktop-card-actions">{renderActions(desktop)}</div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default MacDesktopManager;
